import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2, Download, AlertCircle, FileText, CheckCircle, Clock } from 'lucide-react';
import { EscrowBadge } from './PaymentStatusPanel';
import { formatAmount } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Status colors
const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    viewed: 'bg-purple-100 text-purple-700',
    partially_paid: 'bg-amber-100 text-amber-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500'
};

export default function InvoiceDetailModal({
    open,
    onOpenChange,
    invoice,
    clients = [],   // deal clients (or org clients), passed from parent
    onSend,
    // Mutations from parent
    fundEscrowMutation,
    releaseMutation,
    requestReleaseMutation,
    disputeMutation,
    resolveDisputeMutation,
    withdrawMutation
}) {
    const { user } = useAuth();
    const [resolveReason, setResolveReason] = useState('');
    const [disputeReason, setDisputeReason] = useState('');

    // Local state for modals when dealing with disputes natively here
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [disputeModalOpen, setDisputeModalOpen] = useState(false);

    if (!invoice) return null;

    const isPaid = invoice.status === 'paid';
    const isOverdue = invoice.status === 'overdue' ||
        (invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'paid');

    const isEscrow = !!invoice.deal_id;
    const escrowStatus = invoice.escrow_status || 'pending';

    // Build the set of client IDs that belong to the current logged-in user
    // (mirrors PaymentStatusPanel.js logic exactly)
    const currentUserId = String(user?._id || user?.id || '');
    const myClientIds = new Set(
        (clients || []).filter(c => String(c.linked_user_id) === currentUserId).map(c => String(c._id))
    );

    const isCurrentUserPayer = (
        (invoice.payer_type === 'user' && invoice.payer_id === currentUserId) ||
        (invoice.payer_type === 'client' && myClientIds.has(String(invoice.payer_id)))
    );
    const isCurrentUserPayee = (
        (invoice.payee_type === 'user' && invoice.payee_id === currentUserId) ||
        (invoice.payee_type === 'client' && myClientIds.has(String(invoice.payee_id)))
    );

    // Render logic is carefully matched to not disturb existing functionality
    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0">
                    <DialogHeader className="bg-slate-50 dark:bg-slate-900/50 p-6 border-b shrink-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <DialogTitle className="text-2xl flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                                    <div className="flex items-center gap-2">
                                        Invoice {invoice.invoice_number}
                                        <Badge className={statusColors[isOverdue ? 'overdue' : invoice.status]}>
                                            {isOverdue && invoice.status !== 'paid' ? 'Overdue' : invoice.status.replace('_', ' ')}
                                        </Badge>
                                        {isEscrow && (
                                            <EscrowBadge status={escrowStatus} />
                                        )}
                                    </div>
                                    {invoice.deal_title && (
                                        <div className="text-sm font-normal text-muted-foreground bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded-md hidden sm:block">
                                            {invoice.deal_title}
                                        </div>
                                    )}
                                </DialogTitle>
                                {invoice.deal_title && (
                                    <div className="text-sm font-normal text-muted-foreground mt-1 sm:hidden">
                                        Deal: {invoice.deal_title}
                                    </div>
                                )}
                            </div>
                            {/* <div className="text-right">
                                <p className="text-2xl font-bold">
                                    {formatAmount(invoice.total, invoice.currency)}
                                </p>
                                {invoice.paid_amount > 0 && invoice.paid_amount < invoice.total && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Paid: {formatAmount(invoice.paid_amount, invoice.currency)}
                                    </p>
                                )}
                            </div> */}
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Entities Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white dark:bg-card p-4 rounded-lg border shadow-sm">
                            <div>
                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">From</h4>
                                <p className="font-medium text-sm">{invoice.sender_name || 'System User'}</p>
                                {invoice.sender_email && <p className="text-sm text-muted-foreground">{invoice.sender_email}</p>}
                                {invoice.sender_address && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{invoice.sender_address}</p>}
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Billed To (Payer)</h4>
                                <p className="font-medium text-sm">{invoice.payer_resolved_name || invoice.client_name || 'Client'}</p>
                                {invoice.client_email && <p className="text-sm text-muted-foreground">{invoice.client_email}</p>}
                                {invoice.client_address && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{invoice.client_address}</p>}
                            </div>
                            {isEscrow && (
                                <div>
                                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Payee (Receiving Funds)</h4>
                                    <p className="font-medium text-sm">
                                        {invoice.payee_resolved_name || 'Escrow Target'}
                                    </p>
                                    <p className="text-sm text-muted-foreground truncate" title={invoice.payee_id}>ID: {invoice.payee_id}</p>
                                </div>
                            )}
                        </div>
                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 p-4 rounded-lg">
                            <div>
                                <p className="font-medium text-muted-foreground mb-1">Issue Date</p>
                                <p>{new Date(invoice.created_at).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <p className="font-medium text-muted-foreground mb-1">Due Date</p>
                                <p>{new Date(invoice.due_date).toLocaleDateString()}</p>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div>
                            <h3 className="font-semibold text-lg mb-3">Line Items</h3>
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader className="bg-muted/50">
                                        <TableRow>
                                            <TableHead>Description</TableHead>
                                            <TableHead className="text-right">Qty</TableHead>
                                            <TableHead className="text-right">Price</TableHead>
                                            <TableHead className="text-right">Tax</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(invoice.line_items || []).map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell className="font-medium">
                                                    <p>{item.description}</p>
                                                </TableCell>
                                                <TableCell className="text-right">{item.quantity}</TableCell>
                                                <TableCell className="text-right">{formatAmount(item.unit_price, invoice.currency)}</TableCell>
                                                <TableCell className="text-right">{item.tax_rate}%</TableCell>
                                                <TableCell className="text-right font-medium">
                                                    {formatAmount(
                                                        item.quantity * item.unit_price * (1 - (item.discount || 0) / 100) * (1 + (item.tax_rate || 0) / 100),
                                                        invoice.currency
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Totals Summary */}
                            <div className="flex justify-end mt-4">
                                <div className="w-64 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Subtotal:</span>
                                        <span>{formatAmount(invoice.subtotal, invoice.currency)}</span>
                                    </div>
                                    {invoice.total_tax > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Tax:</span>
                                            <span>{formatAmount(invoice.total_tax, invoice.currency)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-bold text-lg pt-2 border-t mt-2">
                                        <span>Total:</span>
                                        <span>{formatAmount(invoice.total, invoice.currency)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Escrow History (If applicable) */}
                        {isEscrow && invoice.dispute_history && invoice.dispute_history.length > 0 && (
                            <div>
                                <h3 className="font-semibold text-lg mb-3">Escrow Timeline</h3>
                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-md p-4 text-sm space-y-3 border border-slate-100 dark:border-slate-800">
                                    <ul className="space-y-3 relative border-l-2 border-slate-200 dark:border-slate-700 ml-2 pl-4">
                                        {invoice.dispute_history.map((hist, idx) => {
                                            const isDisputed = hist.type === 'disputed';
                                            let disputeNum = 1;
                                            for (let i = 0; i <= idx; i++) {
                                                if (invoice.dispute_history[i].type === 'disputed' && i < idx) {
                                                    disputeNum++;
                                                }
                                            }
                                            return (
                                                <li key={idx} className="relative">
                                                    <div className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border border-white dark:border-card z-10 ${isDisputed ? 'bg-orange-500' : 'bg-emerald-500'}`} />
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className={`font-semibold ${isDisputed ? 'text-orange-600' : 'text-emerald-600'}`}>
                                                            {isDisputed ? `Dispute ${disputeNum}` : `Resolved explanation ${disputeNum - 1}`}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {new Date(hist.timestamp).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-wrap">{hist.reason}</p>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {invoice.notes && (
                            <div>
                                <h3 className="font-semibold text-lg mb-2">Notes</h3>
                                <p className="text-muted-foreground text-sm whitespace-pre-wrap">{invoice.notes}</p>
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t bg-background shrink-0 mt-auto">
                        <DialogFooter className="flex flex-col sm:flex-row items-center justify-between w-full gap-4 sm:gap-0">
                            <Button variant="outline" className="mr-auto w-full sm:w-auto" onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                            <div className="flex flex-wrap justify-end gap-2 w-full sm:w-auto">

                                {/* Standard Non-Escrow Actions */}
                                {!isEscrow && invoice.status === 'draft' && (
                                    <Button onClick={() => onSend(invoice._id)}>
                                        Send Invoice
                                    </Button>
                                )}

                                {/* ESCROW ACTIONS NATIVELY IN INVOICE VIEWER */}
                                {isEscrow && (
                                    <div className="flex gap-2">
                                        {/* FUND */}
                                        {isCurrentUserPayer && ['pending', 'failed', 'checkout_initiated'].includes(escrowStatus) && (
                                            <Button
                                                onClick={() => fundEscrowMutation.mutate(invoice._id)}
                                                disabled={fundEscrowMutation.isPending}
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                {fundEscrowMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                Fund Escrow
                                            </Button>
                                        )}

                                        {/* WITHDRAW FOR PAYEE */}
                                        {isCurrentUserPayee && escrowStatus === 'released' && !invoice.stripe_transfer_id && (
                                            <Button
                                                onClick={() => withdrawMutation.mutate(invoice._id)}
                                                disabled={withdrawMutation.isPending}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                                {withdrawMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                Withdraw Funds to Bank
                                            </Button>
                                        )}

                                        {/* DISPUTE FOR PAYER */}
                                        {isCurrentUserPayer && ['funded', 'release_requested'].includes(escrowStatus) && (
                                            <Button
                                                variant="outline"
                                                className="border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800 hover:border-orange-400"
                                                onClick={() => setDisputeModalOpen(true)}
                                                disabled={disputeMutation?.isPending}
                                            >
                                                Raise Dispute
                                            </Button>
                                        )}

                                        {/* RELEASE FOR PAYER */}
                                        {isCurrentUserPayer && ['funded', 'release_requested'].includes(escrowStatus) && (
                                            <Button
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                onClick={() => releaseMutation.mutate(invoice._id)}
                                                disabled={releaseMutation?.isPending}
                                            >
                                                Release Payment
                                            </Button>
                                        )}

                                        {/* REQUEST RELEASE FOR PAYEE */}
                                        {isCurrentUserPayee && escrowStatus === 'funded' && (
                                            <Button
                                                variant="outline"
                                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-400"
                                                onClick={() => requestReleaseMutation.mutate(invoice._id)}
                                                disabled={requestReleaseMutation?.isPending}
                                            >
                                                Request Release
                                            </Button>
                                        )}

                                        {/* RESOLVE FOR PAYEE */}
                                        {isCurrentUserPayee && escrowStatus === 'disputed' && (
                                            <Button
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                onClick={() => setResolveModalOpen(true)}
                                                disabled={resolveDisputeMutation?.isPending}
                                            >
                                                Resolve and Unfreeze
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dispute Modal for Native Escrow View */}
            {
                isEscrow && isCurrentUserPayer && (
                    <Dialog open={disputeModalOpen} onOpenChange={setDisputeModalOpen}>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-red-600">
                                    <AlertCircle className="h-5 w-5" />
                                    Raise Escrow Dispute
                                </DialogTitle>
                                <DialogDescription>
                                    Explain the issue clearly. The payee will be notified to resolve this.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-3">
                                <div className="space-y-2">
                                    <Label htmlFor="dispute-reason">Reason for dispute <span className="text-red-500">*</span></Label>
                                    <Textarea
                                        id="dispute-reason"
                                        placeholder="E.g., The work delivered does not meet the agreed requirements..."
                                        value={disputeReason}
                                        onChange={(e) => setDisputeReason(e.target.value)}
                                        rows={4}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => {
                                    setDisputeModalOpen(false);
                                    setDisputeReason('');
                                }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => {
                                        if (!disputeReason.trim()) {
                                            toast.error('Please provide a reason for the dispute');
                                            return;
                                        }
                                        disputeMutation.mutate({ invoiceId: invoice._id, reason: disputeReason.trim() }, {
                                            onSuccess: () => {
                                                setDisputeModalOpen(false);
                                                setDisputeReason('');
                                            }
                                        });
                                    }}
                                    disabled={!disputeReason.trim() || disputeMutation?.isPending}
                                >
                                    {disputeMutation?.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Submit Dispute
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )
            }

            {/* Resolve Modal for Native Escrow View */}
            {
                isEscrow && isCurrentUserPayee && (
                    <Dialog open={resolveModalOpen} onOpenChange={setResolveModalOpen}>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-emerald-600">
                                    <CheckCircle className="h-5 w-5" />
                                    Resolve Dispute
                                </DialogTitle>
                                <DialogDescription>
                                    Please provide an explanation to the payer detailing how you resolved their concerns.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-3">
                                <div className="space-y-2">
                                    <Label htmlFor="resolve-reason">Resolution Explanation <span className="text-red-500">*</span></Label>
                                    <Textarea
                                        id="resolve-reason"
                                        placeholder="E.g., I have updated the deliverables based on your feedback..."
                                        value={resolveReason}
                                        onChange={(e) => setResolveReason(e.target.value)}
                                        rows={4}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => {
                                    setResolveModalOpen(false);
                                    setResolveReason('');
                                }}>
                                    Cancel
                                </Button>
                                <Button
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => {
                                        if (!resolveReason.trim()) {
                                            toast.error('Please provide a resolution explanation');
                                            return;
                                        }
                                        resolveDisputeMutation.mutate({ invoiceId: invoice._id, reason: resolveReason.trim() }, {
                                            onSuccess: () => {
                                                setResolveModalOpen(false);
                                                setResolveReason('');
                                            }
                                        });
                                    }}
                                    disabled={!resolveReason.trim() || resolveDisputeMutation?.isPending}
                                >
                                    {resolveDisputeMutation?.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Resolve Dispute
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )
            }
        </>
    );
}
